import type { TooltipContentProps } from "recharts";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ClientOnly } from "~/components/ui/client-only";
import { formatCurrency } from "~/lib/format";
import type { YearSummary } from "~/types/financials";

interface RevenueChartProps {
	years: YearSummary[];
}

function RevenueTooltip({ active, payload, label }: TooltipContentProps) {
	if (!active || !payload?.length) return null;
	const revenue = payload.find((item) => item.dataKey === "revenue")?.value;
	const netProfit = payload.find((item) => item.dataKey === "netProfit")?.value;

	return (
		<div className="glass-panel-strong min-w-44 rounded-xl px-3 py-2.5 text-xs">
			<p className="font-semibold text-text">Rok {String(label)}</p>
			<div className="mt-2 flex flex-col gap-1.5">
				<div className="flex items-center justify-between gap-4">
					<span className="text-muted">Tržby</span>
					<span className="font-mono font-semibold tabular-nums text-chart-1">
						{formatCurrency(typeof revenue === "number" ? revenue : null)}
					</span>
				</div>
				<div className="flex items-center justify-between gap-4">
					<span className="text-muted">Čistý zisk</span>
					<span className="font-mono font-semibold tabular-nums text-chart-2">
						{formatCurrency(typeof netProfit === "number" ? netProfit : null)}
					</span>
				</div>
			</div>
		</div>
	);
}

export function RevenueChart({ years }: RevenueChartProps) {
	const data = years.map((y) => ({
		year: y.year.toString(),
		revenue: y.revenue,
		netProfit: y.netProfit,
	}));

	// Hide the chart entirely when there is no revenue/profit data to plot
	// (e.g. micro entities that disclose only a balance sheet).
	const hasData = data.some((d) => d.revenue != null || d.netProfit != null);
	if (!hasData) return null;

	return (
		<div className="card-surface overflow-hidden rounded-2xl p-6">
			<div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
				<div>
					<p className="label-overline">Vývoj hospodaření</p>
					<h3 className="mt-1 text-lg font-semibold">Tržby a čistý zisk</h3>
				</div>
				<div className="flex w-fit items-center gap-4 text-xs font-medium text-muted">
					<span className="flex items-center gap-1.5">
						<span className="h-2.5 w-2.5 rounded-sm bg-chart-1" /> Tržby
					</span>
					<span className="flex items-center gap-1.5">
						<span className="h-2.5 w-2.5 rounded-sm bg-chart-2" /> Čistý zisk
					</span>
				</div>
			</div>
			<div className="mt-4 [&_.recharts-surface]:outline-none [&_.recharts-surface:focus-visible]:ring-2 [&_.recharts-surface:focus-visible]:ring-primary-500 [&_.recharts-surface:focus-visible]:ring-offset-2">
				<ClientOnly fallback={<div className="h-[300px] animate-pulse rounded bg-muted" />}>
					<ResponsiveContainer width="100%" height={320}>
						<BarChart
							data={data}
							barCategoryGap="32%"
							barGap={4}
							margin={{ top: 16, right: 8, bottom: 8, left: 8 }}
							title="Vývoj tržeb a čistého zisku"
							desc="Srovnání tržeb a čistého zisku v jednotlivých letech"
						>
							<CartesianGrid
								vertical={false}
								strokeDasharray="3 3"
								stroke="var(--color-border)"
								strokeOpacity={0.8}
							/>
							<XAxis
								dataKey="year"
								axisLine={false}
								tickLine={false}
								tick={{ fill: "var(--color-muted)", fontSize: 12 }}
								dy={10}
							/>
							<YAxis
								axisLine={false}
								tickLine={false}
								tick={{ fill: "var(--color-muted)", fontSize: 11 }}
								tickFormatter={(v: number) => formatCurrency(v)}
								width={92}
							/>
							<Tooltip content={RevenueTooltip} cursor={false} />
							<Bar
								dataKey="revenue"
								fill="var(--color-chart-1)"
								maxBarSize={42}
								radius={[6, 6, 0, 0]}
							/>
							<Bar
								dataKey="netProfit"
								fill="var(--color-chart-2)"
								maxBarSize={42}
								radius={[6, 6, 0, 0]}
							/>
						</BarChart>
					</ResponsiveContainer>
				</ClientOnly>
			</div>
		</div>
	);
}
